/**
 * ST-02 Triadic cadence (INV-CH-ST-02; changes C-D1-ST02, C-D2-ST02).
 *
 * Events: TRIAD_RE hits ("clear, concise, and compelling") per prose sentence. The seed's raw-count
 * ramp is removed (D1: it scored 40 at zero triads and 82 at 132 triads in a long text, D2); the
 * rate ramp is the seed's, verbatim.
 */
import {
	compiledEnglish,
	line,
	nativeMeasure,
	proseBlocks,
	quote,
	rateDef,
	sentenceExposure,
} from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

export const st02Def = rateDef({
	id: 'ST-02',
	name: 'Triadic cadence',
	family: 'CHOREO',
	features: [{ id: 'triad', label: 'Triad ("a, b, and c")' }],
	oneWay: true,
	minExposure: 20,
	exposureUnit: 'sentences',
	lang: 'native',
	ramp: [
		[0, 8],
		[0.06, 26],
		[0.12, 48],
		[0.2, 68],
		[0.35, 86],
	],
	unitScale: 1,
	unitLabel: 'triads per sentence',
	phi0: 1.5,
	weight: 1.6,
	nd: 0.5,
	changes: ['C-D1-ST02', 'C-D2-ST02'],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const lex = compiledEnglish();
	const k = new Float64Array(doc.paragraphs.count);
	const e = sentenceExposure(doc);
	const ev = makeEvidence();
	const summary: string[] = [];
	for (const b of proseBlocks(doc)) {
		lex.triad.scanBlock(b.text, b.base, (s, en, _i, m) => {
			k[b.p] = (k[b.p] as number) + 1;
			ev.add(s, en, 0, 1);
			if (summary.length < 10) summary.push(line(quote(m[0]), 'triad'));
		});
	}
	return { k, e, evidence: ev, summary };
}

export const st02: ChannelImpl = rateChannel(st02Def, nativeMeasure(measure));
export { st02Def as def, st02 as impl };
